import { Component, inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { type ModelActionPoint } from '@devices/models';
import { DeviceActionsService, DeviceRemoteActionService } from '@devices/services';
import { MessageComponent, NotificationsService } from '@ui';
import { finalize } from 'rxjs';

import { ActionPointValueSelectorComponent } from '../../action-point-value-selector';

@Component({
  imports: [
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinner,
    MessageComponent,
    ActionPointValueSelectorComponent,
  ],
  selector: 'avs-execute-action-dialog',
  templateUrl: './execute-action-dialog.component.html',
})
export class ExecuteActionDialogComponent {
  protected remoteActionService = inject(DeviceRemoteActionService);
  protected data = inject<{
    actionPoint: ModelActionPoint;
    deviceId: string;
    isRemoteAction: boolean;
    remoteActionPointId: string;
  }>(MAT_DIALOG_DATA);
  protected value = signal<string>('');
  protected isLoading = signal(false);
  private notificationService = inject(NotificationsService);
  private deviceActionsService = inject(DeviceActionsService);
  private dialogRef = inject(MatDialogRef<ExecuteActionDialogComponent>);

  executeRemoteAction() {
    this.isLoading.set(true);
    this.remoteActionService.executeRemoteAction(
      {
        deviceId: this.data.deviceId,
        remoteActionPointId: this.data.remoteActionPointId,
        value: this.value(),
      },
    ).pipe(
      finalize(() => {
        this.isLoading.set(false);
        this.dialogRef.close();
      }),
    ).subscribe({
      error: (err) => {
        this.notificationService.showErrorMessage(err.message);
      },
      next: () => {
        this.notificationService.showSuccessMessage(`Remote action ${this.data.actionPoint.friendlyName} was successfully sent`);
      },
    });
  }

  protected executeDeviceAction() {
    this.isLoading.set(true);
    this.deviceActionsService.sendAction({
      actionPointId: this.data.actionPoint.id,
      deviceId: this.data.deviceId,
      value: this.value(),
    }).pipe(
      finalize(() => {
        this.isLoading.set(false);
        this.dialogRef.close();
      }),
    ).subscribe({
      error: (error) => {
        this.notificationService.showErrorMessage(error.message);
      },
      next: (finalResult) => {
        if (finalResult) {
          const name = this.data.actionPoint.friendlyName || this.data.actionPoint.name;

          this.notificationService.showSuccessMessage(`Device action "${name}" was successfully sent`);
        }
      },
    });
  }
}
